/**
 * Defines versioned contracts for the configurable case classification catalog.
 */
import { z } from "zod";
/**
 * Stable per-level code. It survives renames, so exports and analytics keep
 * meaning after an administrator edits the display name.
 */
export const CaseReasonCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z][A-Z0-9_]{1,39}$/, "Expected a stable uppercase code.");
const reasonNameSchema = z.string().trim().min(1).max(120);
const reasonDescriptionSchema = z.string().trim().max(1_000).nullable();
const sortOrderSchema = z.number().int().min(0).max(10_000);
/**
 * The three levels of the hierarchy, from broadest to narrowest.
 */
export const CaseReasonLevelSchema = z.enum(["CATEGORY", "REASON", "SUB_REASON"]);
const catalogNodeShape = {
    code: CaseReasonCodeSchema,
    createdAt: z.iso.datetime({ offset: true }),
    description: reasonDescriptionSchema,
    id: z.uuid(),
    isActive: z.boolean(),
    name: reasonNameSchema,
    sortOrder: sortOrderSchema,
    updatedAt: z.iso.datetime({ offset: true }),
};
export const CaseSubReasonSchema = z.object({ ...catalogNodeShape, reasonId: z.uuid() }).strict();
export const CaseReasonSchema = z
    .object({
    ...catalogNodeShape,
    categoryId: z.uuid(),
    subReasons: z.array(CaseSubReasonSchema).max(200),
})
    .strict();
export const CaseReasonCategorySchema = z
    .object({ ...catalogNodeShape, reasons: z.array(CaseReasonSchema).max(200) })
    .strict();
/**
 * Whole catalog in one response. The tree is small and bounded by the limits
 * above, so the administration console never paginates it.
 */
export const CaseReasonCatalogResponseSchema = z
    .object({
    categories: z.array(CaseReasonCategorySchema).max(200),
    requiresPublicReason: z.boolean(),
})
    .strict();
export const CaseReasonCatalogQuerySchema = z
    .object({
    includeInactive: z
        .enum(["true", "false"])
        .default("false")
        .transform((value) => value === "true"),
})
    .strict();
export const CreateCaseReasonCategoryRequestSchema = z
    .object({
    code: CaseReasonCodeSchema,
    description: reasonDescriptionSchema.optional(),
    name: reasonNameSchema,
    sortOrder: sortOrderSchema.optional(),
})
    .strict();
export const CreateCaseReasonRequestSchema = CreateCaseReasonCategoryRequestSchema.extend({
    categoryId: z.uuid(),
}).strict();
export const CreateCaseSubReasonRequestSchema = CreateCaseReasonCategoryRequestSchema.extend({
    reasonId: z.uuid(),
}).strict();
/**
 * The code is deliberately absent: it is the stable identity of the node and
 * changing it would silently rewrite the meaning of historical analytics.
 */
export const UpdateCaseReasonNodeRequestSchema = z
    .object({
    description: reasonDescriptionSchema,
    name: reasonNameSchema,
    sortOrder: sortOrderSchema.optional(),
})
    .strict();
export const UpdateCompanyReasonPolicyRequestSchema = z
    .object({ requiresPublicReason: z.boolean() })
    .strict();
/**
 * Classification carried on a case. Every level is nullable and the hierarchy
 * is validated together, never field by field.
 */
export const CaseClassificationSelectionSchema = z
    .object({
    categoryId: z.uuid().nullable(),
    reasonId: z.uuid().nullable(),
    subReasonId: z.uuid().nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.reasonId !== null && value.categoryId === null) {
        context.addIssue({
            code: "custom",
            message: "A reason requires its category.",
            path: ["categoryId"],
        });
    }
    if (value.subReasonId !== null && value.reasonId === null) {
        context.addIssue({
            code: "custom",
            message: "A sub-reason requires its reason.",
            path: ["reasonId"],
        });
    }
});
const classificationNodeSchema = z
    .object({
    code: CaseReasonCodeSchema,
    id: z.uuid(),
    name: reasonNameSchema,
})
    .strict();
/**
 * Resolved classification returned with a case. Names are denormalized so the
 * consoles do not have to join against the catalog to render a row.
 */
export const CaseClassificationSchema = z
    .object({
    category: classificationNodeSchema.nullable(),
    reason: classificationNodeSchema.nullable(),
    subReason: classificationNodeSchema.nullable(),
})
    .strict();
/**
 * Same resolved classification without the database identifier, for the sealed
 * receipt and any other anonymous payload.
 */
export const PublicCaseClassificationSchema = z
    .object({
    category: classificationNodeSchema.omit({ id: true }).nullable(),
    reason: classificationNodeSchema.omit({ id: true }).nullable(),
    subReason: classificationNodeSchema.omit({ id: true }).nullable(),
})
    .strict();
export const SetCaseClassificationRequestSchema = z
    .object({
    categoryId: z.uuid().nullable(),
    reason: z.string().trim().min(5).max(1_000),
    reasonId: z.uuid().nullable(),
    subReasonId: z.uuid().nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.reasonId !== null && value.categoryId === null) {
        context.addIssue({
            code: "custom",
            message: "A reason requires its category.",
            path: ["categoryId"],
        });
    }
    if (value.subReasonId !== null && value.reasonId === null) {
        context.addIssue({
            code: "custom",
            message: "A sub-reason requires its reason.",
            path: ["reasonId"],
        });
    }
});
/**
 * Public projection of the catalog: only active nodes, no audit metadata and
 * deliberately no database identifier. The consumer selects stable codes, which
 * keeps internal UUIDs off the anonymous boundary exactly like every other
 * public payload.
 */
const publicNodeShape = {
    code: CaseReasonCodeSchema,
    name: reasonNameSchema,
};
export const PublicCaseSubReasonSchema = z.object(publicNodeShape).strict();
export const PublicCaseReasonSchema = z
    .object({
    ...publicNodeShape,
    subReasons: z.array(PublicCaseSubReasonSchema).max(200),
})
    .strict();
export const PublicCaseReasonCategorySchema = z
    .object({
    ...publicNodeShape,
    reasons: z.array(PublicCaseReasonSchema).max(200),
})
    .strict();
/**
 * Catalog handed to the public form together with whether it must be answered.
 * An empty list always means optional, whatever the company configured, because
 * a required field the consumer cannot satisfy would close the book.
 */
export const PublicCaseReasonCatalogSchema = z
    .object({
    categories: z.array(PublicCaseReasonCategorySchema).max(200),
    isRequired: z.boolean(),
})
    .strict();
/**
 * Classification chosen in the public form, addressed by stable codes. The
 * server resolves them against the company catalog; an unknown or inactive code
 * is a validation error, never a silently dropped field.
 */
export const PublicCaseClassificationSelectionSchema = z
    .object({
    categoryCode: CaseReasonCodeSchema,
    reasonCode: CaseReasonCodeSchema,
    subReasonCode: CaseReasonCodeSchema.nullable().optional(),
})
    .strict();
export const CaseReasonAnalyticsQuerySchema = z
    .object({
    branchSlug: z.string().trim().min(1).max(120).optional(),
    from: z.iso.date().optional(),
    groupBy: CaseReasonLevelSchema.default("REASON"),
    operationalType: z.enum(["CLAIM", "COMPLAINT"]).optional(),
    status: z.enum(["RECEIVED", "PENDING_ASSIGNMENT", "IN_INVESTIGATION", "CLOSED"]).optional(),
    to: z.iso.date().optional(),
})
    .strict()
    .refine((value) => value.from === undefined || value.to === undefined || value.from <= value.to, "The start date must not be after the end date.");
/**
 * One aggregated bucket. `path` names the ancestors of the grouped level so a
 * chart can label "Facturación › Cobro duplicado" without a second request.
 */
export const CaseReasonAnalyticsBucketSchema = z
    .object({
    caseCount: z.number().int().nonnegative(),
    code: CaseReasonCodeSchema.nullable(),
    id: z.uuid().nullable(),
    name: z.string(),
    path: z.array(z.string()).max(2),
    share: z.number().min(0).max(1),
})
    .strict();
export const CaseReasonAnalyticsResponseSchema = z
    .object({
    buckets: z.array(CaseReasonAnalyticsBucketSchema).max(500),
    groupBy: CaseReasonLevelSchema,
    totalCases: z.number().int().nonnegative(),
    unclassifiedCases: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=CaseReasonContract.js.map